import { createApi } from '@reduxjs/toolkit/query/react';
import { HaveFinish, PriceKey } from 'utils/finishes';
import { formatHasLegality } from 'constants/formats';
import { baseQuery } from '../../baseQuery';
import { MTGCard } from 'types/interfaces/mtgCard';

interface CardsSearchRequest {
  name?: string;
  exact?: boolean;
  oracleText?: string;
  typeLine?: string;
  setName?: string;
  exactSetName?: boolean;
  colors?: string[];
  manaCost?: string;
  cmc?: number;
  cmc_comparison?: 'eq' | 'gt' | 'lt';
  power?: string;
  power_comparison?: 'eq' | 'gt' | 'gte' | 'lt' | 'lte';
  toughness?: string;
  toughness_comparison?: 'eq' | 'gt' | 'gte' | 'lt' | 'lte';
  artist?: string;
  reserved?: boolean;
  flavorText?: string;
  lang?: string;
  format?: string;
  formatLegality?: 'legal' | 'not_legal';
  limit?: number;
  /** Autocomplete narrower, applied before pagination: exact set code, else collector-number prefix, else set-name word start. */
  filter?: string;
  /** Exact collector number (Fast Entry, Asana 1219423341660540). `name` may be empty with it. */
  collector_number?: string;
  sort?: string;
  direction?: 'asc' | 'desc';
  /** Restrict to printings at least one eligible wanter accepts — cards you could send. */
  availableToSend?: boolean;
  /** Restrict to printings at least one eligible holder offers for trade — cards you could receive. */
  availableToReceive?: boolean;
  /** How `colors` matches: superset (default) | equal set | subset. */
  colorsMode?: 'including' | 'exactly' | 'at_most';
  /** Inclusive bounds on the EN point value (100 points = $1). */
  pointsMin?: number;
  pointsMax?: number;
  /** Results per page (max 60) and result shape; see constants/search.ts. */
  pageSize?: number;
  view?: 'printings' | 'cards';
  /** Finish filter: comma list of nonfoil|foil|etched (none = all, several = OR). Builder only. */
  finish?: string;
}

export interface CardsSearchResponse {
  cards: MTGCard[];
  page: number;
  hasMore: boolean;
  totalCount: number;
  /** Echoed by the paginated backend (Asana 1219353862024235); absent on an older one. */
  pageSize?: number;
  view?: 'printings' | 'cards';
}

export interface ConsolidatedCard {
  cardName: string;
  oracleId: string;
  totalVersions: number;
  sampleImage: {
    normal?: string;
    small?: string;
    art_crop?: string;
  };
  lowestPrice?: number;
  setNames?: string[];
}

export interface ConsolidatedSearchResponse {
  cards: ConsolidatedCard[];
  totalResults: number;
  hasMore: boolean;
  page: number;
}

// Backend response for /cards/:id and /cards/by-printing is FLAT: MTGCard fields
// at the top level, with `printings` as a sibling array. There is no `.card` wrapper.
export interface CardDetailResponse extends MTGCard {
  printings?: Array<{
    id: number;
    name: string;
    setName: string;
    collectorNumber: string;
    foil: boolean;
    nonFoil: boolean;
    imageUris: any;
    points?: number;
    rarity: string;
    releasedAt: Date;
  }>;
  // Backend may also include lowercase `scryfallId` alongside the canonical `scryfallID`.
  scryfallId?: string;
  // Community stats, card-scoped (all printings sharing the oracle id). Integers, never
  // null from the backend. Optional ONLY as a deploy-order guard: if the backend hasn't
  // shipped them yet the owned/traded stat segments don't render (never a fake 0).
  ownersCount?: number;
  tradesCount?: number;
}

/**
 * Card-attribute filter params, in two halves so the Search query string keeps
 * its historical order (the trade/colors_mode/points keys have always followed
 * sort/direction). Every list surface that accepts the Search filters sends
 * exactly these names through appendCardFilterParams below; there is one
 * casing map in the codebase and this is it.
 */
export const appendCardFilterParamsHead = (
  searchParams: URLSearchParams,
  params: CardsSearchRequest,
): void => {
  // Add each parameter if it exists
  if (params.name) searchParams.append('name', params.name);
  if (params.exact !== undefined) searchParams.append('exact', params.exact.toString());
  if (params.oracleText) searchParams.append('oracle_text', params.oracleText);
  if (params.typeLine) searchParams.append('type_line', params.typeLine);  
  if (params.setName) searchParams.append('set_name', params.setName);
  if (params.exactSetName !== undefined) searchParams.append('exact_set_name', params.exactSetName.toString());
  if (params.colors && params.colors.length > 0) {
    searchParams.append('colors', params.colors.join(','));
  }
  if (params.manaCost) searchParams.append('mana_cost', params.manaCost);
  if (params.cmc !== undefined) searchParams.append('cmc', params.cmc.toString());
  if (params.cmc_comparison) searchParams.append('cmc_comparison', params.cmc_comparison);
  // Check against '' rather than truthiness: power/toughness of '0' is a real
  // query (Ornithopter and every 0/x), and truthiness dropped the operand while
  // still sending the comparison.
  if (params.power !== undefined && params.power !== '') searchParams.append('power', params.power);
  if (params.power_comparison) searchParams.append('power_comparison', params.power_comparison);
  if (params.toughness !== undefined && params.toughness !== '') searchParams.append('toughness', params.toughness);
  if (params.toughness_comparison) searchParams.append('toughness_comparison', params.toughness_comparison);
  if (params.artist) searchParams.append('artist', params.artist);
  if (params.reserved !== undefined) searchParams.append('reserved', params.reserved.toString());
  if (params.flavorText) searchParams.append('flavor_text', params.flavorText);
  if (params.lang) searchParams.append('lang', params.lang);
  // The backend validates `format` against a lowercase allowlist (@IsIn(MTG_FORMATS)),
  // so anything Title-Cased — including already-crawled ?format=Commander URLs — 400s.
  if (params.format) searchParams.append('format', params.format.toLowerCase());
  // Classic Legacy has no "not legal" side; the backend reads a missing
  // formatLegality as legal, so it is never sent for that format.
  if (params.formatLegality && formatHasLegality(params.format ?? '')) {
    searchParams.append('formatLegality', params.formatLegality);
  }
};

export const appendCardFilterParamsTail = (
  searchParams: URLSearchParams,
  params: CardsSearchRequest,
): void => {
  // NOTE: the wire casing here is deliberately inconsistent and matches the backend
  // exactly — the availableTo* flags are camelCase while colors_mode/points_min/points_max
  // are snake_case. Verified against the live endpoint: the other spelling of each is
  // silently ignored, not rejected. Do not "fix" these to match one another.
  //
  // That failure mode is why these are pinned by searchWire.test.ts: a mis-cased
  // param does not 400, it returns UNFILTERED results, so the filter looks live
  // in the UI while doing nothing at all.
  if (params.availableToSend) searchParams.append('availableToSend', 'true');
  if (params.availableToReceive) searchParams.append('availableToReceive', 'true');
  if (params.colorsMode) searchParams.append('colors_mode', params.colorsMode);
  if (params.pointsMin !== undefined) searchParams.append('points_min', params.pointsMin.toString());
  if (params.pointsMax !== undefined) searchParams.append('points_max', params.pointsMax.toString());
  if (params.finish) searchParams.append('finish', params.finish);
};

/** Both halves, for endpoints with no sort/direction/filter keys in between. */
export const appendCardFilterParams = (
  searchParams: URLSearchParams,
  params: CardsSearchRequest,
): void => {
  appendCardFilterParamsHead(searchParams, params);
  appendCardFilterParamsTail(searchParams, params);
};

export const buildSearchParams = (params: CardsSearchRequest & { page: number }): string => {
  const searchParams = new URLSearchParams();

  appendCardFilterParamsHead(searchParams, params);
  if (params.filter) searchParams.append('filter', params.filter);
  if (params.collector_number) searchParams.append('collector_number', params.collector_number);
  if (params.sort) searchParams.append('sort', params.sort);
  if (params.direction) searchParams.append('direction', params.direction);
  appendCardFilterParamsTail(searchParams, params);

  // Always add page and limit (increased default for better UX). The Search page
  // also sends pageSize (the new name, capped at 60) and view; limit stays as the
  // alias an older backend reads.
  searchParams.append('page', params.page.toString());
  searchParams.append('limit', (params.limit || 40).toString());
  if (params.pageSize) searchParams.append('pageSize', params.pageSize.toString());
  if (params.view) searchParams.append('view', params.view);
  
  return searchParams.toString();
};

interface EstimatePriceRequest {
  cardId: string;
  language: string;
  condition: string;
  /** Authoritative (Track D); sent with the derived `foil` through D */
  finish?: HaveFinish;
  foil: boolean;
}

interface EstimatePriceData {
  price: number;
  languageMultiplierApplied: boolean;
  multiplierValue: number;
  /** The Price column used (backend Track A); null when no price resolved. */
  priceKey?: PriceKey | null;
  /** Echoed finish (Track D) */
  finish?: HaveFinish;
}

/**
 * Stable cache key for a search request.
 *
 * A plain JSON.stringify is key-order sensitive, so two structurally identical
 * filter sets built in different orders became two cache entries and two network
 * requests. Sorting the keys makes the key depend on the values alone. Callers
 * should still normalize array order (e.g. colors) so equal filters compare equal.
 */
export const serializeCardsSearchArgs = (queryArgs: CardsSearchRequest): string =>
  JSON.stringify(queryArgs, Object.keys(queryArgs).sort());

export const cardsSearchApi = createApi({
  reducerPath: 'cardsSearchApi',
  tagTypes: ['CardsSearch'],
  baseQuery: baseQuery,
  endpoints: (builder) => ({
    cardsSearch: builder.infiniteQuery<
      CardsSearchResponse,
      CardsSearchRequest,
      number,
      string
    >({
      query: ({ pageParam, queryArg }) => ({
        url: `/cards/search-light?${buildSearchParams({ ...queryArg, page: pageParam })}`,
        method: 'GET',
        requiresAuth: false, // Public endpoint - no authentication required for SEO
      }),
      serializeQueryArgs: ({ queryArgs, endpointName }) =>
        `${endpointName}(${serializeCardsSearchArgs(queryArgs)})`,
      providesTags: (_, __, arg) => [
        { type: 'CardsSearch', id: serializeCardsSearchArgs(arg) }, // scoped to the search params
      ],
      infiniteQueryOptions: {
        initialPageParam: 1,
        getNextPageParam: (lastPage) => {
          return lastPage.hasMore ? lastPage.page + 1 : undefined;
        },
      },
    }),
    // Get single card by ID
    getCardById: builder.query<CardDetailResponse, string | number>({
      query: (cardId) => {
        console.log('🔍 Fetching card with ID:', cardId);
        return {
          url: `/cards/${cardId}`,
          method: 'GET',
          requiresAuth: false, // Public endpoint - no authentication required for SEO
        };
      },
      providesTags: (_, __, id) => [{ type: 'CardsSearch', id: `card-${id}` }],
    }),
    // Get all printings of a card
    getCardPrintings: builder.query<MTGCard[], { cardId: string | number; languages?: string[] }>({
      query: ({ cardId, languages }) => ({
        url: `/cards/${cardId}/printings${languages?.length ? `?languages=${languages.join(',')}` : ''}`,
        method: 'GET',
        requiresAuth: false, // Public endpoint - no authentication required for SEO
      }),
      providesTags: (_, __, { cardId, languages }) => [
        { type: 'CardsSearch', id: `printings-${cardId}-${languages?.join(',') || 'default'}` },
      ],
    }),
    // Regular search endpoint for single page results
    searchCards: builder.query<CardsSearchResponse, CardsSearchRequest & { page?: number }>({
      query: (params) => ({
        url: `/cards/search-light?${buildSearchParams({ ...params, page: params.page || 1 })}`,
        method: 'GET',
        requiresAuth: false, // Public endpoint - no authentication required for SEO
      }),
      providesTags: ['CardsSearch'],
    }),
    // Consolidated search endpoint for deduplicated results
    searchConsolidated: builder.infiniteQuery<
      ConsolidatedSearchResponse,
      CardsSearchRequest,
      number,
      string
    >({
      query: ({ pageParam, queryArg }) => ({
        url: `/cards/search-consolidated?${buildSearchParams({ ...queryArg, page: pageParam })}`,
        method: 'GET',
        requiresAuth: false, // Public endpoint - no authentication required for SEO
      }),
      providesTags: (_, __, arg) => [
        { type: 'CardsSearch', id: `consolidated-${JSON.stringify(arg)}` },
      ],
      infiniteQueryOptions: {
        initialPageParam: 1,
        getNextPageParam: (lastPage) => {
          return lastPage.hasMore ? lastPage.page + 1 : undefined;
        },
      },
    }),
    // Get single card by set code + collector number (slug URL lookup)
    getCardByPrinting: builder.query<CardDetailResponse, { setCode: string; collectorNumber: string }>({
      query: ({ setCode, collectorNumber }) => ({
        url: `/cards/by-printing?set=${encodeURIComponent(setCode)}&number=${encodeURIComponent(collectorNumber)}`,
        method: 'GET',
        requiresAuth: false,
      }),
      providesTags: (_, __, { setCode, collectorNumber }) => [
        { type: 'CardsSearch', id: `printing-${setCode}-${collectorNumber}` },
      ],
    }),
    // Estimate price for a single card with language multiplier info
    estimatePrice: builder.mutation<EstimatePriceData, EstimatePriceRequest>({
      query: (data) => ({
        url: '/cards/estimate-price',
        method: 'POST',
        body: data,
        requiresAuth: true,
      }),
      transformResponse: (response: { success: boolean; data: EstimatePriceData }) => response.data,
    }),
  }),
});

export const {
  useCardsSearchInfiniteQuery,
  useSearchCardsQuery,
  useGetCardByIdQuery,
  useLazyGetCardByIdQuery,
  useGetCardByPrintingQuery,
  useGetCardPrintingsQuery,
  useSearchConsolidatedInfiniteQuery,
  useEstimatePriceMutation,
} = cardsSearchApi;
